import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import Modal from '../modal/Modal';
import { launchSupportChat } from '../../utils/openSupportChat';

interface GetHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HELP_CENTER_URL = 'https://cx.televeda.com/hc/help/en';
const SUPPORT_PHONE = '+1 (833) 299-1449';
const SUPPORT_SMS_PHONE = '+1 (833) 550-5242';
const SUPPORT_EMAIL = 'support@televeda.com';
const TERMS_URL = 'https://www.televeda.com/terms-of-use';
const PRIVACY_URL = 'https://www.televeda.com/privacy-notice';

const toDialableNumber = (phone: string) => phone.replace(/[^\d+]/g, '');
const SUPPORT_PHONE_HREF = `tel:${toDialableNumber(SUPPORT_PHONE)}`;
const SUPPORT_SMS_HREF = `sms:${toDialableNumber(SUPPORT_SMS_PHONE)}`;

const FLIP_TRANSITION_MS = 600;

function FlipCard({
  frontIcon,
  frontLabel,
  backHref,
  backTitle,
  backSubtitle,
  backDetail,
}: {
  frontIcon: string;
  frontLabel: string;
  backHref: string;
  backTitle: string;
  backSubtitle: string;
  backDetail: string;
}) {
  const [flipped, setFlipped] = useState(false);
  const isAnimatingRef = useRef(false);

  function toggleFlip() {
    if (isAnimatingRef.current) return;
    isAnimatingRef.current = true;
    setFlipped((v) => !v);
    window.setTimeout(() => {
      isAnimatingRef.current = false;
    }, FLIP_TRANSITION_MS);
  }

  return (
    <div
      className={`card-flip h-100${flipped ? ' is-flipped' : ''}`}
      onClick={toggleFlip}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          toggleFlip();
        }
      }}
      role="button"
      tabIndex={0}
      aria-pressed={flipped}
    >
      <div className="card-inner h-100">
        <div className="card card-front u-pl-4 u-pr-4">
          <div className="row h-100">
            <div className="col align-self-center u-text-center">
              <i
                aria-hidden
                className={`fa-jelly-duo fa-regular ${frontIcon} fa-2x u-color-primary u-mb-1`}
              />
              <h5 className="size-h5 u-text-bold u-color-primary">{frontLabel}</h5>
            </div>
          </div>
        </div>
        <a
          href={backHref}
          className="card card-back u-pl-4 u-pr-4 text-reset"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <div className="row h-100">
            <div className="col align-self-center u-text-center">
              <h5 className="size-h5 u-text-bold u-color-primary">{backTitle}</h5>
              <p className="mb-0 text-muted">{backSubtitle}</p>
              <p className="small">{backDetail}</p>
            </div>
          </div>
        </a>
      </div>
    </div>
  );
}

export default function GetHelpModal({ isOpen, onClose }: GetHelpModalProps) {
  const { t } = useTranslation();

  function handleLiveChat() {
    onClose();
    launchSupportChat();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size='lg'
      header={(titleId) => (
        <div className="row w-100">
          <div className="col u-text-center u-ml-5">
            <i
              aria-hidden
              className="fa-jelly-duo fa-regular fa-circle-question fa-3x u-color-primary"
            />
            <h2 id={titleId} className="size-h2 u-text-bold u-mt-1">
              {t('GET_HELP')}
            </h2>
          </div>
        </div>
      )}
    >
      <div className="row u-mb-2">
        <div className="col">
          <div className="card u-bg-primary-lightest u-pl-4 u-pr-4">
            <div className="row">
              <div className="col align-self-center">
                <h4 className="size-h4 u-text-bold u-mb-1">{t('VISIT_HELP_CENTER')}</h4>
                <p className="large">{t('VISIT_HELP_CENTER_DESC')}</p>
              </div>
              <div className="col-12 col-md-auto u-text-center align-self-center">
                <a
                  href={HELP_CENTER_URL}
                  className="btn btn-primary u-mt-2-sm"
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('LEARN_MORE')}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row u-mb-2">
        <div className="col-12 col-md">
          <FlipCard
            frontIcon="fa-phone"
            frontLabel={t('CALL')}
            backHref={SUPPORT_PHONE_HREF}
            backTitle={SUPPORT_PHONE}
            backSubtitle={t('AVAILABLE_HOURS')}
            backDetail={t('ARIZONA_TIME')}
          />
        </div>
        <div className="col-12 col-md">
          <FlipCard
            frontIcon="fa-mobile"
            frontLabel={t('TEXT')}
            backHref={SUPPORT_SMS_HREF}
            backTitle={SUPPORT_SMS_PHONE}
            backSubtitle={t('AVAILABLE_HOURS')}
            backDetail={t('ARIZONA_TIME')}
          />
        </div>
        <div className="col-12 col-md">
          <a href={`mailto:${SUPPORT_EMAIL}`} className="card card-front u-pl-4 u-pr-4 h-100">
            <div className="row h-100">
              <div className="col align-self-center u-text-center">
                <i
                  aria-hidden
                  className="fa-jelly-duo fa-regular fa-envelope fa-2x u-color-primary u-mb-1"
                />
                <h5 className="size-h5 u-text-bold u-color-primary">{t('EMAIL')}</h5>
              </div>
            </div>
          </a>
        </div>
        <div className="col-12 col-md">
          <div
            className="card u-pl-4 u-pr-4 h-100 u-cursor-pointer"
            role="button"
            tabIndex={0}
            onClick={handleLiveChat}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleLiveChat();
              }
            }}
          >
            <div className="row h-100">
              <div className="col align-self-center u-text-center">
                <i
                  aria-hidden
                  className="fa-jelly-duo fa-regular fa-comment-dots fa-2x u-color-primary u-mb-1"
                />
                <h5 className="size-h5 u-text-bold u-color-primary">{t('START_LIVE_CHAT')}</h5>
                <p className="small">{t('AVAILABLE_HOURS_AZ')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row">
        <div className="col u-text-center">
          <p>
            {/* These anchors are deliberately empty — <Trans> injects their link text
                from the translation string, so jsx-a11y can't see the content statically. */}
            <Trans
              i18nKey="GET_HELP_SMS_CONSENT"
              components={{
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                privacy: <a href={PRIVACY_URL} target="_blank" rel="noreferrer" />,
                // eslint-disable-next-line jsx-a11y/anchor-has-content
                terms: <a href={TERMS_URL} target="_blank" rel="noreferrer" />,
              }}
            />
          </p>
        </div>
      </div>
    </Modal>
  );
}
